import styled from "styled-components/native";
import { useEffect, useState } from "react";
import React from "react";
import { DefaultTheme } from "styled-components/native";
import InputDrawer from "@components/molecules/InputDrawer/InputDrawer";
import { FlatList } from "react-native";
import { Button, VDBText } from "@components/atoms";
import RadioButton from "@components/atoms/RadioButton";
import { VDBIcons } from "@utils/VDBIcons";
import { SpaceType } from "@components/atoms/Spacer/types";
import { css } from "styled-components/native";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { ProductCategory } from "@utils/constants";
import { DisplayConfigurationV2 } from "@src/types/DiamondDetail";
import NativeDropDown from "../JewelryBuilderOptions/JewelryBuilderDropDown/NativeDropDown";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";

type OrderNowDropDownProps = {
  productType: ProductCategory;
  configObject: DisplayConfigurationV2;
  onSelect?: (optionName: string, value: string) => void;
  defaultLabel?: string;
  value?: string;
  showValidation?: boolean;
};

const OrderNowDropDown = ({
  productType,
  configObject,
  onSelect,
  defaultLabel,
  value,
  showValidation = false,
}: OrderNowDropDownProps) => {
  const { theme, t, typography } = useScreenOptionsHooks();
  const [selectedItem, setSelectedItem] = useState(value ?? defaultLabel ?? "");

  useEffect(() => {
    if (value !== undefined && value !== selectedItem) {
      setSelectedItem(value);
    }
  }, [value]);

  const enabledOptions = configObject.option_value.filter((opt) => opt.enabled);

  const isSingleOption = enabledOptions.length === 1;
  const singleOptionLabel = isSingleOption
    ? enabledOptions[0]?.label ?? defaultLabel ?? ""
    : "";
  const selectedOption = enabledOptions.find(
    (opt) =>
      opt.label === selectedItem ||
      opt.value === selectedItem ||
      opt.slug === selectedItem
  );
  const nativeValue = selectedOption
    ? selectedOption.value || selectedOption.slug
    : "";
  const isPlaceholderSelection =
    !selectedItem || selectedItem.trim().toLowerCase() === "please select";
  const showError =
    showValidation &&
    !isSingleOption &&
    (!selectedOption || isPlaceholderSelection);
  const pleaseConfigureMessage = t(
    "searchResult.jewelry.pleaseConfigure.text",
    "Please configure"
  );
  const displayItemText = selectedItem
    ? t(selectedItem)
    : t("searchResult.jewelry.pleaseSelect.text", "Please Select");

  useEffect(() => {
    if (isSingleOption && singleOptionLabel) {
      onSelect?.(configObject.option_name, singleOptionLabel);
    }
  }, []);

  const handleSelect = (mValue: string) => {
    setSelectedItem(mValue);
    onSelect?.(configObject.option_name, mValue);
  };

  const DropDownContent = ({ closeModal }: { closeModal: () => void }) => {
    return (
      <>
        <FlatList
          nestedScrollEnabled
          data={enabledOptions}
          keyExtractor={(item, index) => `${item.value}-${index}`}
          style={{ padding: 12, paddingHorizontal: 16 }}
          renderItem={({ item }) => {
            return (
              <>
                <RadioButton
                  label={item.label}
                  checked={selectedItem === item.label}
                  labelColor={
                    selectedItem === item.value
                      ? theme["core-05"]
                      : theme["core-06"]
                  }
                  onPress={() => {
                    closeModal();
                    handleSelect(item?.label);
                  }}
                />
              </>
            );
          }}
        />
        <ButtonContainer>
          <ContainerOverlay>
            <Button
              type={ButtonType.primary}
              category={ButtonCategory.default}
              title={t("global.text.done")}
              onPress={closeModal}
            />
          </ContainerOverlay>
        </ButtonContainer>
      </>
    );
  };

  const errorMessage = showError ? (
    <ErrorText>
      <VDBText type={typography["Minor-R"]} color={theme["error-01"]}>
        {pleaseConfigureMessage}
      </VDBText>
    </ErrorText>
  ) : null;

  return (
    <ContainerView>
      <LabelContainer>
        <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
          {configObject.option_name}
        </VDBText>
      </LabelContainer>
      <DropDownContainer>
        {isSingleOption ? (
          <SingleValueContainer>
            <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
              {t(singleOptionLabel)}
            </VDBText>
          </SingleValueContainer>
        ) : theme.isLargeScreen ? (
          <NativeDropDownWrapper>
            <NativeDropDown
              defaultValue={nativeValue}
              options={enabledOptions}
              productType={productType}
              isSubOptions={false}
              showError={showError}
              height={32}
              onSelect={(_value, item) => handleSelect(item?.label || _value)}
            />
          </NativeDropDownWrapper>
        ) : (
          <InputDrawer
            children={<DropDownContent closeModal={() => {}} />}
            itemText={displayItemText}
            headerTitle={t(configObject.option_name)}
            iconName={VDBIcons.FILLED_CIRCLE_DOWN}
            iconColor={theme["main-01"]}
            borderRadius={8}
            height={32}
            showError={showError}
          />
        )}
        {errorMessage}
      </DropDownContainer>
    </ContainerView>
  );
};

const LabelContainer = styled.View`
  width: 35%;
  justify-content: center;
  min-height: 32px;
`;

const DropDownContainer = styled.View`
  width: 65%;
  min-height: 32px;
  justify-content: center;
`;

const ContainerView = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 8px;
  padding: 8px 16px 0;
  width: 100%;
  flex-direction: row;
  align-items: flex-start;
`;

const ErrorText = styled.View`
  margin-top: 6px;
`;

const ButtonContainer = styled.View`
  padding-top: ${SpaceType.Big}px;
  padding-bottom: ${SpaceType.ExtraLarge}px;
  padding-horizontal: ${SpaceType.Double}px;
  align-items: center;
  ${({ theme }) =>
    !theme.isMobile &&
    css`
      padding-horizontal: 0px;
    `}
`;

const ContainerOverlay = styled.View`
  width: 100%;
  ${({ theme }) =>
    !theme.isMobile &&
    css`
      max-width: 343px;
    `}
`;

const SingleValueContainer = styled.View`
  width: 100%;
  min-height: 32px;
  justify-content: center;
  padding-left: 10px;
`;

const NativeDropDownWrapper = styled.View`
  height: 32px;
`;

export default OrderNowDropDown;
